/**
 * SSL-индикатор для Installation (v0.9.62 #9.55).
 *
 * Цветовая логика:
 *  - серый «—»: ssl_expires_at не задан (никогда не проверяли).
 *  - зелёный: остаётся > 30 дней.
 *  - жёлтый: ≤ 30 дней.
 *  - красный: ≤ 7 дней или просрочен.
 *  - оранжевый «?»: ssl_last_error не пуст (cert невалиден).
 *
 * Клик → POST /check-ssl, перезагрузка карточки.
 */
import { useState } from 'react';
import { Lock, LockOpen, AlertTriangle, RefreshCw } from 'lucide-react';
import { api } from '../api/client';
import type { Installation } from '../api/organizations';

export function SslBadge({ inst, orgId, onChanged }: {
  inst: Installation; orgId: string; onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const daysLeft = inst.ssl_expires_at
    ? Math.ceil((new Date(inst.ssl_expires_at).getTime() - Date.now()) / 86400000)
    : null;

  let color = 'text-slate-500';
  let icon = <Lock size={10} />;
  let label = 'SSL —';
  let title = 'SSL не проверялся. Кликните для проверки.';

  if (inst.ssl_last_error && !daysLeft) {
    color = 'text-amber-300';
    icon = <AlertTriangle size={10} />;
    label = 'SSL?';
    title = `SSL не получен: ${inst.ssl_last_error}`;
  } else if (daysLeft !== null) {
    if (daysLeft < 0) {
      color = 'text-rose-300';
      icon = <LockOpen size={10} />;
      label = `просрочен ${-daysLeft}д`;
      title = `Сертификат истёк ${-daysLeft} дней назад. Выпустил: ${inst.ssl_issuer || '—'}`;
    } else if (daysLeft <= 7) {
      color = 'text-rose-300';
      icon = <AlertTriangle size={10} />;
      label = `${daysLeft}д`;
      title = `Сертификат истекает через ${daysLeft} дней. Выпустил: ${inst.ssl_issuer || '—'}`;
    } else if (daysLeft <= 30) {
      color = 'text-amber-300';
      icon = <AlertTriangle size={10} />;
      label = `${daysLeft}д`;
      title = `Сертификат истекает через ${daysLeft} дней. Выпустил: ${inst.ssl_issuer || '—'}`;
    } else {
      color = 'text-emerald-300';
      icon = <Lock size={10} />;
      label = `${daysLeft}д`;
      title = `SSL ОК, истекает через ${daysLeft} дней. Выпустил: ${inst.ssl_issuer || '—'}`;
    }
    if (inst.ssl_last_error) {
      // Cert получен но invalid — оранжевый
      color = 'text-amber-300';
      title += ` ⚠ ${inst.ssl_last_error}`;
    }
  }

  const onClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/v1/organizations/${orgId}/installations/${inst.id}/check-ssl`);
      onChanged();
    } catch (err: any) {
      alert('SSL check error: ' + (err?.response?.data?.detail || err?.message || 'ошибка'));
    } finally { setBusy(false); }
  };

  return (
    <button onClick={onClick} disabled={busy} title={title}
            className={`inline-flex items-center gap-0.5 text-[10px] ${color} hover:opacity-80 px-1 py-0.5 rounded border border-current/30`}>
      {busy ? <RefreshCw size={10} className="animate-spin" /> : icon}
      {label}
    </button>
  );
}
